/**
 * LeadFly Runtime — Analytics, A/B testing, and page setup.
 *
 * Loaded as a deferred extension asset from leadfly-gate.liquid (or
 * sections/leadfly-gate.liquid pushed via themeFilesUpsert). Reads all
 * per-funnel config from the <script type="application/json" id="lf-config">
 * block injected by publish-assembly.server.tsx into section-0.
 *
 * Exposes window.__lf.init(earlyVariant?) so the section-0 inline bootstrap
 * can trigger it synchronously if this file has already been parsed.
 */
(function () {
  'use strict';

  // ── Config reader ─────────────────────────────────────────────────────────

  function getConfig() {
    try {
      var el = document.getElementById('lf-config');
      return el ? JSON.parse(el.textContent || el.innerText || '{}') : {};
    } catch (e) { return {}; }
  }

  // ── Helper: hide Shopify theme chrome ─────────────────────────────────────
  // CSS already handles the initial hide; this JS pass covers dynamically
  // injected elements (e.g. sticky headers inserted after page load).

  function hideElements() {
    var selectors = [
      'header', 'footer',
      '.header', '.footer',
      '#header', '#footer',
      '[class*="header"]', '[class*="footer"]',
      '.site-header', '.site-footer',
      '.main-header', '.main-footer'
    ];
    selectors.forEach(function (selector) {
      try {
        document.querySelectorAll(selector).forEach(function (el) {
          el.style.setProperty('display', 'none', 'important');
        });
      } catch (e) {}
    });
  }

  // ── Helper: center the COD / lead form ───────────────────────────────────

  var _formCentered = false;

  function centerExternalLeadForm() {
    if (_formCentered) return null;
    var selectors = [
      '#lfcod-main', '.lfcod-form',
      '[id*="webi_leadform_order_cod_form"]',
      '.shopify-app-block[data-block-handle="leadform"]',
      '[id*="-app-leadform"]', '.leadform-section',
      'form[action*="leadform"]', 'form[action*="cod"]',
      '[data-leadform]', '[data-form-type="lead"]',
      '[class*="leadform"]', '[class*="lead-form"]',
      '[id*="leadform"]', '[id*="lead-form"]',
      'form[class*="lead"]', 'form[id*="lead"]',
      'form:not([action*="search"]):not([action*="login"]):not([action*="subscribe"])',
      '.form-container form', '.lead-capture', '.contact-form'
    ];

    function centerForm(formElement) {
      formElement.style.cssText = formElement.style.cssText +
        'max-width:800px;width:100%;margin:0 auto;display:block;';
      var appBlock = formElement.closest('.shopify-app-block');
      if (appBlock) {
        appBlock.style.cssText = appBlock.style.cssText +
          'display:flex;justify-content:center;width:100%;';
      }
    }

    function findAndCenterForm() {
      for (var i = 0; i < selectors.length; i++) {
        try {
          var els = document.querySelectorAll(selectors[i]);
          for (var j = 0; j < els.length; j++) {
            var el = els[j];
            if (el.offsetParent !== null || getComputedStyle(el).display !== 'none') {
              centerForm(el);
              _formCentered = true;
              return el;
            }
          }
        } catch (e) {}
      }
      return null;
    }

    var form = findAndCenterForm();
    if (!form) {
      setTimeout(function () {
        form = findAndCenterForm();
        if (!form) setTimeout(findAndCenterForm, 2000);
      }, 1000);
    }
    return form;
  }

  // ── Helper: COD form page-width CSS ──────────────────────────────────────

  function addPageWidthOverrideCSS(cfg, abVariantAssigned) {
    if (document.querySelector('#leadfly-page-width-override')) return;
    if (cfg.isAb && !abVariantAssigned) return;
    var ns = cfg.leadFormNameSpace;
    if (!ns) return;
    var style = document.createElement('style');
    style.id = 'leadfly-page-width-override';
    style.textContent =
      '[id*="' + ns + '"]{display:flex!important;flex-direction:column!important;align-items:center!important;padding:0!important}' +
      '[id*="' + ns + '"] .page-width{max-width:1024px!important;width:100%!important;margin:0!important}' +
      '[id*="' + ns + '"] .shopify-app-block{max-width:800px!important;margin:0 auto!important;display:flex!important;justify-content:center!important;width:100%!important;overflow:visible!important}' +
      '[id*="' + ns + '"] .shopify-app-block > *{width:100%!important;overflow:visible!important}';
    document.head.appendChild(style);
  }

  // ── Helper: COD form section inline styles ────────────────────────────────

  function handleCodFormStyling(cfg, abVariantAssigned) {
    if (cfg.isAb && !abVariantAssigned) return;
    var ns = cfg.leadFormNameSpace;
    if (!ns) return;
    var section = document.querySelector('[id*="' + ns + '"]');
    if (!section) return;
    section.style.setProperty('display', 'flex', 'important');
    section.style.setProperty('flex-direction', 'column', 'important');
    section.style.setProperty('align-items', 'center', 'important');
    section.style.setProperty('padding', '0', 'important');
    var pageWidth = section.querySelector('.page-width');
    if (pageWidth) {
      pageWidth.style.setProperty('max-width', '1024px', 'important');
      pageWidth.style.setProperty('width', '100%', 'important');
      pageWidth.style.setProperty('margin', '0', 'important');
      if (cfg.codFormBackground) pageWidth.style.background = cfg.codFormBackground;
      var pad = cfg.codFormPadding;
      // Guard prevents stacking a new listener on every applyVariant call.
      if (pad && !pageWidth._lfResizeAttached) {
        pageWidth._lfResizeAttached = true;
        var applyPadding = function () {
          var p = window.innerWidth >= 800 ? pad.desktop : pad.mobile;
          pageWidth.style.setProperty('padding-top', p.paddingTop + 'px', 'important');
          pageWidth.style.setProperty('padding-bottom', p.paddingBottom + 'px', 'important');
          pageWidth.style.setProperty('padding-left', p.paddingLeft + 'px', 'important');
          pageWidth.style.setProperty('padding-right', p.paddingRight + 'px', 'important');
        };
        applyPadding();
        window.addEventListener('resize', applyPadding);
      }
    }
    var appBlock = section.querySelector('.shopify-app-block');
    if (appBlock) {
      appBlock.style.setProperty('max-width', '800px', 'important');
      appBlock.style.setProperty('margin', '0 auto', 'important');
      appBlock.style.setProperty('display', 'flex', 'important');
      appBlock.style.setProperty('justify-content', 'center', 'important');
      appBlock.style.setProperty('width', '100%', 'important');
    }
  }

  // ── Main init ─────────────────────────────────────────────────────────────

  function init(earlyVariant) {
    var cfg = getConfig();
    if (!cfg.funnelId) return;

    var funnelId = cfg.funnelId;
    var beaconUrl = cfg.beaconUrl || '';
    var assignUrl = cfg.assignUrl || '';
    var isAb = !!cfg.isAb;
    var leadFormNameSpace = cfg.leadFormNameSpace || '';
    var namespace = cfg.namespace || '';
    var codIndexInBFiltered = cfg.codIndexInB || 0;
    var _abVariantAssigned = !isAb;

    // ── Visitor ID ──────────────────────────────────────────────────────────

    var visitorKey = 'lf_visitor_id';
    var visitorId = null;
    try { visitorId = localStorage.getItem(visitorKey); } catch (e) {}
    if (!visitorId) {
      visitorId = 'v_' + Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
      try { localStorage.setItem(visitorKey, visitorId); } catch (e) {}
    }

    // ── Analytics beacon ────────────────────────────────────────────────────

    function sendBeacon(variant, event) {
      try {
        var ua = navigator.userAgent || '';
        var device = 'Desktop';
        if (/Mobi|Android|iPhone|iPod/i.test(ua)) device = 'Mobile';
        else if (/Tablet|iPad|PlayBook|Silk/i.test(ua)) device = 'Tablet';

        var ref = document.referrer || '';
        var search = window.location.search || '';
        var source = 'Direct';
        if (/utm_source=tiktok|tiktok\.com/i.test(search + ' ' + ref)) {
          source = 'TikTok Ads';
        } else if (/utm_source=(facebook|instagram)|facebook\.com|instagram\.com|fb\.me/i.test(search + ' ' + ref)) {
          source = 'Meta Ads';
        } else if (ref !== '') {
          try { source = new URL(ref).hostname.replace('www.', '') || 'Organic'; }
          catch (e) { source = 'Organic'; }
        }

        var os = 'Other';
        if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';
        else if (/Android/i.test(ua)) os = 'Android';
        else if (/Windows/i.test(ua)) os = 'Windows';
        else if (/Macintosh|Mac OS X/i.test(ua)) os = 'macOS';
        else if (/Linux/i.test(ua)) os = 'Linux';

        fetch(beaconUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            funnelId: funnelId,
            variant: variant ? variant.toUpperCase() : null,
            event: event,
            visitorId: visitorId,
            device: device,
            source: source,
            os: os
          }),
          keepalive: true
        });
      } catch (e) {}
    }

    // ── A/B: reposition COD form for variant B ──────────────────────────────

    function repositionCodFormForB() {
      try {
        var codWrapper = document.querySelector('[id*="' + leadFormNameSpace + '"]');
        if (!codWrapper) return;
        // Old architecture: separate B sections with -b- in their ID.
        // New merged architecture: no B-specific sections — fall back to all
        // content sections (merged, using A namespaces).
        var bSections = Array.from(document.querySelectorAll('[id*="' + namespace + '-b-"]'));
        var sections = bSections.length
          ? bSections
          : Array.from(document.querySelectorAll('[id*="' + namespace + '-"]'))
              .filter(function (el) { return el.id.indexOf(leadFormNameSpace) === -1; });
        if (!sections.length) return;
        var idx = Math.min(Math.max(codIndexInBFiltered - 1, 0), sections.length - 1);
        var after = sections[idx];
        if (after.nextSibling) {
          after.parentNode.insertBefore(codWrapper, after.nextSibling);
        } else {
          after.parentNode.appendChild(codWrapper);
        }
      } catch (e) {}
    }

    // ── A/B: apply variant to DOM ───────────────────────────────────────────

    var _assignedVariant = null;

    function applyVariant(variant) {
      _assignedVariant = variant;
      _abVariantAssigned = true;
      var ctx = document.getElementById('lf-ab-context');
      if (ctx) ctx.setAttribute('data-variant', variant || '');
      document.querySelectorAll('[data-ab-variant]').forEach(function (el) {
        el.style.display = el.getAttribute('data-ab-variant') === variant ? 'block' : 'none';
      });
      if (variant === 'b') repositionCodFormForB();
      if (cfg.hasAppSection) {
        addPageWidthOverrideCSS(cfg, _abVariantAssigned);
        handleCodFormStyling(cfg, _abVariantAssigned);
      }
    }

    // ── A/B: stream-in observer (reveals sections as HTML parser adds them) ─

    var variantObserver = null;
    if (isAb) {
      variantObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            var els = [];
            if (node.getAttribute && node.getAttribute('data-ab-variant')) els.push(node);
            if (node.querySelectorAll) {
              Array.prototype.forEach.call(
                node.querySelectorAll('[data-ab-variant]'),
                function (el) { els.push(el); }
              );
            }
            var v = _assignedVariant;
            els.forEach(function (el) {
              el.style.display = el.getAttribute('data-ab-variant') === v ? 'block' : 'none';
            });
          });
        });
      });
      variantObserver.observe(document.documentElement, { childList: true, subtree: true });
    }

    // ── Form conversion tracking ────────────────────────────────────────────

    var _trackingInitialized = false;
    var _convSent = false;

    function processForms() {
      var activeVar = _assignedVariant;
      document.querySelectorAll('form').forEach(function (form) {
        function addHidden(name, value) {
          if (!form.querySelector('input[name="' + name + '"]')) {
            var inp = document.createElement('input');
            inp.type = 'hidden'; inp.name = name; inp.value = value;
            form.appendChild(inp);
          }
        }
        addHidden('note_attributes[lf_funnel_id]', funnelId);
        addHidden('attributes[lf_funnel_id]', funnelId);
        addHidden('note_attributes[lf_visitor_id]', visitorId);
        addHidden('attributes[lf_visitor_id]', visitorId);
        if (activeVar) {
          var inpVar = form.querySelector('input[name="note_attributes[lf_ab_variant]"]');
          if (!inpVar) {
            inpVar = document.createElement('input');
            inpVar.type = 'hidden';
            inpVar.name = 'note_attributes[lf_ab_variant]';
            form.appendChild(inpVar);
          }
          inpVar.value = activeVar.toUpperCase();
          var inpVarAttr = form.querySelector('input[name="attributes[lf_ab_variant]"]');
          if (!inpVarAttr) {
            inpVarAttr = document.createElement('input');
            inpVarAttr.type = 'hidden';
            inpVarAttr.name = 'attributes[lf_ab_variant]';
            form.appendChild(inpVarAttr);
          }
          inpVarAttr.value = activeVar.toUpperCase();
        }
        if (form._lfWatched) return;
        form._lfWatched = true;
        form.addEventListener('submit', function () {
          if (_convSent) return;
          _convSent = true;
          sendBeacon(_assignedVariant, 'conversion_attempt');
        });
      });
    }

    function activate(variant) {
      if (isAb) {
        // Full DOM pass — sections 1+ are now parsed (init runs after defer).
        applyVariant(variant);
        if (document.readyState === 'loading') {
          // Still parsing: final pass + disconnect when done.
          document.addEventListener('DOMContentLoaded', function () {
            applyVariant(variant);
            if (variantObserver) variantObserver.disconnect();
          });
        } else {
          // DOMContentLoaded already fired (deferred-load path): disconnect now.
          if (variantObserver) variantObserver.disconnect();
        }
      }

      if (_trackingInitialized) return;
      _trackingInitialized = true;
      sendBeacon(variant, 'view');

      var formObserver = new MutationObserver(processForms);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
          processForms();
          formObserver.disconnect();
        });
      } else {
        processForms();
        formObserver.disconnect();
      }
      // Brief observation window to catch forms injected by Shopify app blocks
      // that render asynchronously just after DOMContentLoaded.
      if (document.body) {
        formObserver.observe(document.body, { childList: true, subtree: true });
        setTimeout(function () { formObserver.disconnect(); }, 5000);
      }
    }

    // ── Page setup ──────────────────────────────────────────────────────────

    hideElements();
    centerExternalLeadForm();
    if (cfg.hasAppSection) {
      addPageWidthOverrideCSS(cfg, _abVariantAssigned);
      handleCodFormStyling(cfg, _abVariantAssigned);
    }

    // Observer to hide/center as theme injects elements dynamically
    var _observerDone = false;
    var setupObserver = new MutationObserver(function () {
      if (_observerDone) { setupObserver.disconnect(); return; }
      if (document.readyState !== 'loading') {
        hideElements();
        var found = centerExternalLeadForm();
        if (found) {
          if (cfg.hasAppSection) {
            addPageWidthOverrideCSS(cfg, _abVariantAssigned);
            handleCodFormStyling(cfg, _abVariantAssigned);
          }
          _observerDone = true;
          setupObserver.disconnect();
        }
      }
    });
    setupObserver.observe(document.body, { childList: true, subtree: true });

    // ── A/B variant assignment ──────────────────────────────────────────────

    if (isAb) {
      var key = 'lf_ab_' + funnelId;
      var stored = null;
      try { stored = localStorage.getItem(key); } catch (e) {}

      // earlyVariant was chosen by the section-0 inline bootstrap (or from
      // localStorage). We trust it and validate in the background.
      var candidate = earlyVariant || stored || (Math.random() < 0.5 ? 'a' : 'b');
      if (!stored) {
        try { localStorage.setItem(key, candidate); } catch (e) {}
      }
      activate(candidate);

      // Background validation / re-balancing
      var hint = stored ? '&hint=' + stored : '';
      fetch(assignUrl + '?funnelId=' + encodeURIComponent(funnelId) + hint)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var confirmed = data.variant || candidate;
          try { localStorage.setItem(key, confirmed); } catch (e) {}
          if (confirmed !== candidate) applyVariant(confirmed);
        })
        .catch(function () {});
    } else {
      activate(null);
    }
  }

  // ── Expose and auto-init ──────────────────────────────────────────────────

  window.__lf = window.__lf || {};
  window.__lf.init = init;

  // If the section-0 bootstrap already ran and is waiting for us, call init now.
  if (window.__lfBootReady) {
    init(window.__lfEarlyVariant);
  }
})();
